import React, { useEffect, useState } from "react";
import { FlaskConical, Loader2, Play, Trash2, RefreshCw, Clock, Coins } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STAGE = { scene: "تجهيز الصور المفتاحية", animate: "تحريك اللقطات", stitch: "الدمج والصوت", done: "اكتمل" };

const LabVideo = ({ r }) => {
  const [err, setErr] = useState(false);
  if (err) return <div className="p-4 text-center text-xs text-destructive" data-testid={`costlab-video-missing-${r.variant}`}>تعذّر تحميل الفيديو (ربما فُقد بعد إعادة تشغيل الخادم) — احذف العيّنة وأعد التجربة</div>;
  return <video src={`${API}/admin/cost-lab/${r.id}/video`} controls playsInline onError={() => setErr(true)} className="h-full w-full object-contain" data-testid={`costlab-video-${r.variant}`} />;
};

const RunCard = ({ r, onDelete }) => (
  <div className="rounded-2xl border border-border bg-secondary/30 overflow-hidden flex flex-col" data-testid={`costlab-run-${r.variant}`}>
    <div className="p-3 border-b border-border">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-extrabold">{r.label}</div>
          <div className="text-[11px] text-muted-foreground">{r.model}</div>
        </div>
        <button onClick={() => onDelete(r.id)} className="text-muted-foreground hover:text-destructive transition-colors" data-testid={`costlab-delete-${r.id}`} aria-label="حذف"><Trash2 className="w-4 h-4" /></button>
      </div>
      <div className="mt-2 flex items-center gap-3 text-xs flex-wrap">
        <span className="px-2 py-0.5 rounded-full bg-primary/15 text-primary font-bold flex items-center gap-1" data-testid={`costlab-cost-${r.variant}`}><Coins className="w-3 h-3" /> {r.total_sar} ريال / فيديو</span>
        {r.seconds ? <span className="text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" /> {Math.round(r.seconds)} ث</span> : null}
        {r.music_track ? <span className="text-muted-foreground">موسيقى: {r.music_track}</span> : null}
      </div>
      {r.project_name ? <div className="mt-1 text-[11px] text-muted-foreground" data-testid={`costlab-source-${r.variant}`}>المشروع: {r.project_name}{r.product_name ? ` · ${r.product_name}` : ""}{r.has_product_ref ? " · صورة منتج ✓" : ""}{r.has_logo ? " · شعار ✓" : ""}</div> : null}
    </div>
    <div className="aspect-[9/16] max-h-[420px] bg-black/60 flex items-center justify-center relative">
      {r.status === "completed" ? (
        <LabVideo r={r} />
      ) : r.status === "failed" ? (
        <div className="p-4 text-center text-xs text-destructive" data-testid={`costlab-error-${r.variant}`}>فشلت التجربة: {r.error}</div>
      ) : (
        <div className="p-4 text-center text-xs text-muted-foreground space-y-2" data-testid={`costlab-progress-${r.variant}`}>
          <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto" />
          <div>{STAGE[r.stage] || "جاري التنفيذ"} · {r.progress || 0}%</div>
        </div>
      )}
    </div>
  </div>
);

export const AdminCostLab = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState(["runway", "fal_wan", "kenburns"]);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [script, setScript] = useState("");
  const [gender, setGender] = useState("");

  const load = async () => {
    try { const r = await api.get("/admin/cost-lab"); setData(r.data); }
    catch { toast.error("تعذّر تحميل مختبر التكلفة"); }
  };
  useEffect(() => { load(); api.get("/admin/cost-lab/projects").then((r) => setProjects(r.data.projects || [])).catch(() => {}); }, []);
  const running = (data?.runs || []).some((r) => r.status === "processing");
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [running]);

  const run = async () => {
    setBusy(true);
    try { await api.post("/admin/cost-lab/run", { variants: sel, project_id: projectId || null, script: script || null, gender: gender || null }); toast.success("بدأت التجارب — تظهر النتائج هنا تباعًا"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "تعذّر بدء التجربة"); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    try { await api.delete(`/admin/cost-lab/${id}`); setData((d) => ({ ...d, runs: d.runs.filter((r) => r.id !== id) })); }
    catch { toast.error("تعذّر الحذف"); }
  };

  return (
    <section className="rounded-2xl border border-border bg-card p-5 space-y-4" data-testid="admin-cost-lab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-extrabold font-heading flex items-center gap-2"><FlaskConical className="w-5 h-5 text-primary" /> مختبر التكلفة — عيّنات تجريبية</h2>
          <p className="text-xs text-muted-foreground mt-1">
            بريف ثابت: <b className="text-foreground">{data?.brief?.product || "ثوب عراقة"}</b> · نفس السكربت ونفس القواعد السعودية الصارمة لكل الخيارات. هذه تجارب للأدمن فقط ولا تغيّر ما يستلمه العملاء.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} className="gap-1 border-border" data-testid="costlab-refresh"><RefreshCw className="w-3 h-3" /> تحديث</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {(data?.variants || []).map((v) => (
          <label key={v.id} className={`rounded-xl border p-3 cursor-pointer transition-colors ${sel.includes(v.id) ? "border-primary bg-primary/10" : "border-border bg-secondary/30"}`} data-testid={`costlab-variant-${v.id}`}>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={sel.includes(v.id)} onChange={(e) => setSel((s) => (e.target.checked ? [...s, v.id] : s.filter((x) => x !== v.id)))} className="accent-primary" />
              <span className="font-bold text-sm">{v.label}</span>
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">{v.model}</div>
            <div className="text-xs mt-2 flex items-center gap-2 flex-wrap">
              <span className="font-extrabold text-primary">{v.total_sar} ريال</span>
              <span className="text-muted-foreground line-through">{v.old_total_sar} ريال</span>
              <span className="text-[10px] text-muted-foreground">(قبل مكتبة الموسيقى)</span>
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">{v.notes}</div>
          </label>
        ))}
      </div>

      <div className="rounded-xl border border-border bg-secondary/20 p-3 grid grid-cols-1 lg:grid-cols-[1fr_1fr_auto] gap-3" data-testid="costlab-source">
        <label className="text-xs space-y-1">
          <span className="text-muted-foreground">مصدر البريف والمراجع (سكربت العميل + صورة المنتج + الشعار)</span>
          <select value={projectId} onChange={(e) => setProjectId(e.target.value)} className="w-full h-9 rounded-md bg-secondary border border-border px-2 text-sm" data-testid="costlab-project-select">
            <option value="">البريف التجريبي الافتراضي — ثوب عراقة</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.name}{p.product_name ? ` — ${p.product_name}` : ""}{p.has_product_ref ? " · صورة منتج ✓" : ""}{p.has_logo ? " · شعار ✓" : ""}</option>
            ))}
          </select>
        </label>
        <label className="text-xs space-y-1">
          <span className="text-muted-foreground">السكربت (اختياري — يُلتزم به حرفيًا؛ فارغ = وصف المشروع)</span>
          <textarea value={script} onChange={(e) => setScript(e.target.value)} rows={2} data-optional="true" placeholder="اكتب سكربت الإعلان كما يريده العميل…" className="w-full rounded-md bg-secondary border border-border px-2 py-1.5 text-sm" data-testid="costlab-script-input" />
        </label>
        <label className="text-xs space-y-1">
          <span className="text-muted-foreground">الشخصية</span>
          <select value={gender} onChange={(e) => setGender(e.target.value)} className="h-9 rounded-md bg-secondary border border-border px-2 text-sm block" data-testid="costlab-gender-select">
            <option value="">تلقائي</option>
            <option value="male">رجل سعودي</option>
            <option value="female">امرأة سعودية</option>
          </select>
        </label>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <Button onClick={run} disabled={busy || running || !sel.length} className="gap-2" data-testid="costlab-run-btn">
          {busy || running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} {running ? "تجربة قيد التنفيذ…" : "ولّد عيّنات تجريبية"}
        </Button>
        {data && !data.music_ready && <span className="text-xs text-amber-500">مكتبة الموسيقى فارغة — ولّد المقاطع أولًا وإلا ستُولَّد الموسيقى بتكلفة.</span>}
      </div>

      {!data ? <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : data.runs.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="costlab-empty">لا توجد عيّنات بعد.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="costlab-runs">
          {data.runs.map((r) => <RunCard key={r.id} r={r} onDelete={del} />)}
        </div>
      )}
    </section>
  );
};
